import { SpiritLance } from '../systems/combat/weapons/SpiritLance.js';
import { ScatterCannon } from '../systems/combat/weapons/ScatterCannon.js';
import { ArcNeedle } from '../systems/combat/weapons/ArcNeedle.js';
import { OrbitWard } from '../systems/combat/weapons/OrbitWard.js';
import { NovaPulse } from '../systems/combat/weapons/NovaPulse.js';
import { Resonor } from '../systems/combat/weapons/Resonor.js';
import { UmbralLantern } from '../systems/combat/weapons/UmbralLantern.js';
import { Glyphpen } from '../systems/combat/weapons/Glyphpen.js';
import { Countersong } from '../systems/combat/weapons/Countersong.js';

/** Weapons offerable as "new weapon" level-up picks (if not already owned). */
export const NEW_WEAPON_POOL = [
  { name: 'Spirit Lance', rarity: 'common', description: 'Auto-aim piercing bolt.', create: () => new SpiritLance() },
  { name: 'Scatter Cannon', rarity: 'common', description: 'Short-range cone burst toward the boss.', create: () => new ScatterCannon() },
  { name: 'Arc Needle', rarity: 'common', description: 'Fast, low-damage homing darts.', create: () => new ArcNeedle() },
  { name: 'Orbit Ward', rarity: 'common', description: 'Shards orbit you, damaging the boss on contact.', create: () => new OrbitWard() },
  { name: 'Nova Pulse', rarity: 'common', description: 'Periodic AoE burst — rewards staying close.', create: () => new NovaPulse() },
  // Act II additions (ACT2-BIBLE.md §7) — offerable in every mode from run
  // start, not gated behind reaching the Undersong.
  { name: 'Resonor', rarity: 'common', description: 'Beat-locked bursts; the first shot of each hits hardest.', create: () => new Resonor() },
  { name: 'Umbral Lantern', rarity: 'common', description: 'A short beam that also lights the dark.', create: () => new UmbralLantern() },
  // Act III additions (ACT3-BIBLE.md §7), on Act II's terms: they shipped as
  // Quill's and Fugue's starters only, which left 7 of 9 champions unable to
  // see either weapon in any run and made those two starters the only weapons
  // in the game that could never fuse out of the distinct-name damage
  // soft-cap. Offerable from run start, in every mode, like everything above.
  { name: 'Glyphpen', rarity: 'common', description: 'Writes damaging marks on the floor around you.', create: () => new Glyphpen() },
  { name: 'Countersong', rarity: 'common', description: 'A close arc that turns enemy bullets back on them.', create: () => new Countersong() },
];

/**
 * Passive stat boons offerable as level-up picks — in-run only, stack with
 * meta upgrades. Every entry is `stackable: false`: each can be picked at
 * most once per run (see ProgressionSystem's `_ownedNonStackablePassives`
 * dedupe), and every multiplier-based `apply()` combines multiplicatively
 * with whatever else already touched that field (`x *= 1 + bonus` rather
 * than `x += bonus`) rather than additively — flat-percent fields (`?? 1`/
 * `?? 0` fallbacks guard test mocks missing a field, same pattern
 * CombatSystem.js uses for player-field reads).
 */
export const PASSIVE_POOL = [
  {
    id: 'vigor',
    label: 'Vigor',
    rarity: 'common',
    stackable: false,
    description: '+10% max HP, heals to match.',
    apply: (player) => {
      const bonus = player.maxHp * 0.10;
      player.maxHp += bonus;
      player.hp = Math.min(player.maxHp, player.hp + bonus);
    },
  },
  {
    id: 'swift',
    label: 'Swift',
    rarity: 'common',
    stackable: false,
    description: '+8% move speed.',
    apply: (player) => {
      player.moveSpeedMultiplier = (player.moveSpeedMultiplier ?? 1) * 1.08;
    },
  },
  {
    id: 'power',
    label: 'Power',
    rarity: 'common',
    stackable: false,
    description: '+10% weapon damage.',
    apply: (_player, combat) => {
      combat.damageMultiplier = (combat.damageMultiplier ?? 1) * 1.10;
    },
  },
  {
    id: 'overcharge',
    label: 'Overcharge',
    rarity: 'uncommon',
    stackable: false,
    description: '+20% weapon damage.',
    apply: (_player, combat) => {
      combat.damageMultiplier = (combat.damageMultiplier ?? 1) * 1.20;
    },
  },
  {
    id: 'windswept',
    label: 'Windswept',
    rarity: 'uncommon',
    stackable: false,
    description: '+12% move speed, +10% dash range.',
    apply: (player) => {
      player.moveSpeedMultiplier = (player.moveSpeedMultiplier ?? 1) * 1.12;
      player.dashRangeMultiplier = (player.dashRangeMultiplier ?? 1) * 1.10;
    },
  },
  {
    id: 'vitalSurge',
    label: 'Vital Surge',
    rarity: 'uncommon',
    stackable: false,
    description: '+20% max HP, heals to match.',
    apply: (player) => {
      const bonus = player.maxHp * 0.20;
      player.maxHp += bonus;
      player.hp = Math.min(player.maxHp, player.hp + bonus);
    },
  },
  {
    id: 'berserker',
    label: 'Berserker',
    rarity: 'rare',
    stackable: false,
    description: '+20% weapon damage, +20% fire rate.',
    apply: (player, combat) => {
      combat.damageMultiplier = (combat.damageMultiplier ?? 1) * 1.20;
      player.fireRateMultiplier = (player.fireRateMultiplier ?? 1) * 1.20;
    },
  },
  {
    id: 'recklessVigor',
    label: 'Ironheart',
    rarity: 'rare',
    stackable: false,
    description: '+30% max HP, heals to match, takes 15% less incoming damage.',
    apply: (player) => {
      const bonus = player.maxHp * 0.30;
      player.maxHp += bonus;
      player.hp = Math.min(player.maxHp, player.hp + bonus);
      player.damageReduction = 1 - (1 - (player.damageReduction ?? 0)) * (1 - 0.15);
    },
  },
  {
    id: 'vampiric',
    label: 'Vampiric',
    rarity: 'rare',
    stackable: false,
    description: 'Heal 1% of damage dealt to the boss and minions.',
    apply: (player) => {
      player.lifestealPercent = 0.01;
    },
  },
  {
    id: 'executioner',
    label: 'Executioner',
    rarity: 'uncommon',
    stackable: false,
    description: '+30% damage against minions.',
    apply: (player) => {
      player.minionDamageMultiplier = (player.minionDamageMultiplier ?? 1) * 1.30;
    },
  },
  {
    id: 'momentum',
    label: 'Momentum',
    rarity: 'uncommon',
    stackable: false,
    description: 'Move speed ramps up the longer you go unhit (up to +25%), resets on taking damage.',
    apply: (player) => {
      player.momentumRampPerSec = 0.02;
    },
  },
  {
    id: 'adrenaline',
    label: 'Adrenaline',
    rarity: 'rare',
    stackable: false,
    description: '+30% move speed and +20% damage while below 50% HP.',
    apply: (player) => {
      player.hasAdrenaline = true;
    },
  },
  {
    id: 'rapidFire',
    label: 'Rapid Fire',
    rarity: 'uncommon',
    stackable: false,
    description: '+15% weapon fire rate.',
    apply: (player) => {
      player.fireRateMultiplier = (player.fireRateMultiplier ?? 1) * 1.15;
    },
  },
  // --- cards with behavior of their own -------------------------------
  // Everything above is a flat multiplier. The entries below either open a
  // domain nothing else in the pool touches (grazing, full HP, kills, wave
  // clears, fresh targets) or are deliberately weaker echoes of a pet unique
  // (data/pets.js), so a run can reach crit/burn/execute/boss-damage without
  // the matching pet equipped — always at a fraction of the pet's number, so
  // the pet stays the stronger, permanent source. Each writes a flag an
  // existing system already reads, through the same `?? 1` / `?? 0` idiom.
  {
    id: 'bulletdancer',
    label: 'Bulletdancer',
    rarity: 'uncommon',
    stackable: false,
    description: 'Each bullet grazed gives +4% damage for 4s, stacking up to +20%.',
    apply: (player) => {
      player.grazeDamagePerStack = 0.04;
      player.grazeMaxStacks = 5;
    },
  },
  {
    id: 'pristineEdge',
    label: 'Pristine Edge',
    rarity: 'uncommon',
    stackable: false,
    description: '+25% weapon damage while above 80% HP.',
    apply: (player) => {
      player.pristineDamageBonus = 0.25;
    },
  },
  {
    id: 'openingGambit',
    label: 'Opening Gambit',
    rarity: 'common',
    stackable: false,
    description: '+25% damage against enemies still at full HP.',
    apply: (player) => {
      player.fullHpTargetDamageBonus = 0.25;
    },
  },
  {
    id: 'cascade',
    label: 'Cascade',
    rarity: 'rare',
    stackable: false,
    description: 'Every minion killed detonates for 20 damage to those around it.',
    apply: (player) => {
      player.killBlastDamage = 20;
    },
  },
  {
    id: 'spoilsOfWar',
    label: 'Spoils of War',
    rarity: 'common',
    stackable: false,
    description: 'Clearing a wave stage restores 8% max HP.',
    apply: (player) => {
      player.waveClearHealPercent = 0.08;
    },
  },
  {
    id: 'hairtrigger',
    label: 'Hairtrigger',
    rarity: 'rare',
    stackable: false,
    // Keen Edge (mythical pet) is +12% / +40% and scales with copies.
    description: '7% chance to crit for 1.75x damage.',
    apply: (player) => {
      player.cardCritChance = (player.cardCritChance ?? 0) + 0.07;
      player.cardCritDamageBonus = Math.max(player.cardCritDamageBonus ?? 0, 0.75);
    },
  },
  {
    id: 'cinders',
    label: 'Cinders',
    rarity: 'uncommon',
    stackable: false,
    // Ignite (fire legendary pet) is 18% and scales to 36%.
    description: 'Hits burn for an extra 7% of their damage over 3s.',
    apply: (_player, combat) => {
      combat.ignitePercent = (combat.ignitePercent ?? 0) + 0.07;
    },
  },
  {
    id: 'reapersMark',
    label: "Reaper's Mark",
    rarity: 'rare',
    stackable: false,
    // Judgment (rainbow pet) executes under 15%, up to 30%.
    description: 'Instantly finishes non-boss enemies below 8% HP.',
    apply: (player) => {
      // Highest threshold wins rather than summing — two execute sources
      // shouldn't quietly add into a huge one.
      player.executeThreshold = Math.max(player.executeThreshold ?? 0, 0.08);
    },
  },
  {
    id: 'giantsbane',
    label: 'Giantsbane',
    rarity: 'uncommon',
    stackable: false,
    // Giant Slayer (mythical pet) is +25%, up to +50%.
    description: '+12% damage against bosses.',
    apply: (player) => {
      player.bossDamageMultiplier = (player.bossDamageMultiplier ?? 1) * 1.12;
    },
  },
  {
    id: 'reprisal',
    label: 'Reprisal',
    rarity: 'uncommon',
    stackable: false,
    // Retribution (rainbow pet) is 25 damage, up to 50.
    description: 'Taking a hit blasts nearby enemies for 12 damage.',
    apply: (player) => {
      player.reprisalDamage = 12;
    },
  },
  {
    id: 'fieldMedic',
    label: 'Field Medic',
    rarity: 'common',
    stackable: false,
    // Regrowth (moss legendary pet) is +100%, up to +200%.
    description: 'Health pods heal 40% more.',
    apply: (player) => {
      player.podHealBonus = (player.podHealBonus ?? 0) + 0.40;
    },
  },
  {
    id: 'bulwark',
    label: 'Bulwark',
    rarity: 'uncommon',
    stackable: false,
    description: '-12% incoming damage.',
    apply: (player) => {
      // Multiplicative combination with any other damageReduction source
      // (e.g. Ironheart) — asymptotically approaches but never reaches 100%.
      player.damageReduction = 1 - (1 - (player.damageReduction ?? 0)) * (1 - 0.12);
    },
  },
];

/**
 * Pacts: opt-in boon+drawback cards mixed into the level-up draw (see
 * ProgressionSystem._buildChoices — offered from level PACT_MIN_LEVEL on,
 * at most PACT_MAX_PER_RUN accepted per run). Unlike Ascensions (pre-run,
 * global difficulty), a pact is a mid-run gamble taken with full knowledge
 * of the build so far. Every cost writes a flag that an existing system
 * reads through the same `?? 1` / `?? 0`-guarded idiom passives use —
 * nothing here reaches into another system directly. All entries are
 * one-per-run via the same non-stackable dedupe set passives go through.
 */
export const PACT_MIN_LEVEL = 3;
export const PACT_MAX_PER_RUN = 2;

export const PACT_POOL = [
  {
    id: 'glassSoul',
    label: 'Glass Soul',
    rarity: 'pact',
    stackable: false,
    boonText: '+35% weapon damage',
    costText: '-25% max HP',
    description: '+35% weapon damage, but -25% max HP.',
    apply: (player, combat) => {
      combat.damageMultiplier = (combat.damageMultiplier ?? 1) * 1.35;
      player.maxHp = Math.max(1, player.maxHp * 0.75);
      player.hp = Math.min(player.hp, player.maxHp);
    },
  },
  {
    id: 'hunger',
    label: 'Hunger',
    rarity: 'pact',
    stackable: false,
    boonText: '+2.5% lifesteal',
    costText: 'Health Pods stop spawning',
    description: 'Heal 2.5% of damage dealt, but Health Pods stop spawning.',
    apply: (player) => {
      player.lifestealPercent = (player.lifestealPercent ?? 0) + 0.025;
      player.podSpawnDisabled = true; // read by PickupSystem's spawn gate; pods already up stay collectible
    },
  },
  {
    id: 'overload',
    label: 'Overload',
    rarity: 'pact',
    stackable: false,
    boonText: '+25% fire rate',
    costText: 'Dash cooldown +0.5s',
    description: '+25% weapon fire rate, but dash recharges 0.5s slower.',
    apply: (player) => {
      player.fireRateMultiplier = (player.fireRateMultiplier ?? 1) * 1.25;
      player.dashCooldown += 0.5;
    },
  },
  {
    id: 'gildedPact',
    label: 'Gilded Pact',
    rarity: 'pact',
    stackable: false,
    boonText: '+50% Echoes this run',
    costText: 'Bosses attack 15% more often',
    description: '+50% Echoes earned this run, but bosses pick attacks 15% faster.',
    apply: (player) => {
      player.echoPactMultiplier = (player.echoPactMultiplier ?? 1) * 1.5; // folded into both echo payouts in Game.js
      player.bossPickSpeedMultiplier = (player.bossPickSpeedMultiplier ?? 1) * 1.15; // read by SpawningSystem._startNextPattern
    },
  },
  {
    id: 'phasewalker',
    label: 'Phasewalker',
    rarity: 'pact',
    stackable: false,
    boonText: '+1 dash charge',
    costText: '-30% max HP',
    description: 'An extra dash charge, but -30% max HP.',
    apply: (player) => {
      player.addDashCharge();
      player.maxHp = Math.max(1, player.maxHp * 0.70);
      player.hp = Math.min(player.hp, player.maxHp);
    },
  },
  {
    id: 'crescendo',
    label: 'Crescendo',
    rarity: 'pact',
    stackable: false,
    boonText: 'Ultimate charges 50% faster',
    costText: 'Your ultimate lasts 40% less time',
    description: 'The ultimate meter charges 50% faster, but each ultimate window is shorter.',
    apply: (player) => {
      player.ultimateChargeGainMultiplier = (player.ultimateChargeGainMultiplier ?? 1) * 1.5;
      player.ultimateDurationMultiplier = (player.ultimateDurationMultiplier ?? 1) * (5 / 8);
    },
  },
  {
    id: 'recklessPact',
    label: 'Reckless Pact',
    rarity: 'pact',
    stackable: false,
    boonText: '+40% weapon damage',
    costText: 'Getting hit drains 15% of the ultimate meter',
    description: '+40% weapon damage, but every hit taken drains 15% of the ultimate meter.',
    apply: (player, combat) => {
      combat.damageMultiplier = (combat.damageMultiplier ?? 1) * 1.40;
      player.ultimateDrainOnHit = (player.ultimateDrainOnHit ?? 0) + 0.15;
    },
  },
  {
    id: 'ironPact',
    label: 'Iron Pact',
    rarity: 'pact',
    stackable: false,
    boonText: '-25% incoming damage',
    costText: '-15% move speed',
    description: 'Take 25% less damage, but move 15% slower.',
    apply: (player) => {
      player.damageReduction = 1 - (1 - (player.damageReduction ?? 0)) * (1 - 0.25);
      player.moveSpeedMultiplier = (player.moveSpeedMultiplier ?? 1) * 0.85;
    },
  },
];

export const RARITY_WEIGHTS = { common: 70, uncommon: 25, rare: 5 };

/**
 * Weight table used when the "Fortune's Favor" Shard perk (see
 * data/shardUnlocks.js's `luckyOdds`) is owned — a flat 50/40/10 split
 * instead of RARITY_WEIGHTS' 70/25/5, so uncommon becomes the single
 * biggest bucket rather than just cutting into common's share.
 */
const LUCKY_RARITY_WEIGHTS = { common: 50, uncommon: 40, rare: 10 };

function luckyWeights() {
  return LUCKY_RARITY_WEIGHTS;
}

// Pact and fusion cards ride the same weighted draw but live outside the
// base tables — rollRarity also consumes those tables to size weapon-upgrade
// deltas, and a weapon upgrade must never roll "pact" or "fusion". Pact
// weight 4 sits just under rare (5): they surface now and then without
// crowding the draw. Fusion weight 12 is deliberately high — the offer only
// exists once two weapons are maxed, and a player who earned it shouldn't
// wait five levels to see it.
const PACT_WEIGHT = 4;
const PACT_WEIGHT_LUCKY = 8;
const FUSION_WEIGHT = 12;

function withPactWeight(base, luckyOdds) {
  return { ...base, pact: luckyOdds ? PACT_WEIGHT_LUCKY : PACT_WEIGHT, fusion: FUSION_WEIGHT };
}

/**
 * Rolls a single rarity tier using the same distribution weightedSample
 * draws pool entries from — used by ProgressionSystem to decide how big a
 * weapon-upgrade offer is (common +1 / uncommon +2 / rare +3) so weapon
 * upgrades participate in the same luck curve as new-weapon/passive picks
 * instead of always being the safe, fixed "+1".
 *
 * `random` is the run's level-up stream on a seeded run (BULLET-HELL.md §D8,
 * core/Rng.js) and plain `Math.random` otherwise — an unseeded run must roll
 * exactly as it always has, which is why every seeded call site threads a
 * function rather than this module reaching for a global generator.
 */
export function rollRarity(luckyOdds = false, random = Math.random) {
  const weights = luckyOdds ? luckyWeights() : RARITY_WEIGHTS;
  const entries = Object.entries(weights);
  const totalWeight = entries.reduce((sum, [, weight]) => sum + weight, 0);
  let roll = random() * totalWeight;
  for (const [rarity, weight] of entries) {
    roll -= weight;
    if (roll <= 0) return rarity;
  }
  return entries[entries.length - 1][0];
}

/**
 * Picks `count` distinct entries from `pool` without replacement, weighted
 * by each entry's `rarity` (see RARITY_WEIGHTS) — common picks show up far
 * more often than rare ones, but every level-up still draws from the whole
 * pool rather than a fixed common/uncommon/rare slot layout.
 *
 * `random` is the seeded stream where one is threaded in — see rollRarity.
 */
export function weightedSample(pool, count, luckyOdds = false, random = Math.random) {
  const weights = withPactWeight(luckyOdds ? luckyWeights() : RARITY_WEIGHTS, luckyOdds);
  const remaining = [...pool];
  const picked = [];
  while (picked.length < count && remaining.length > 0) {
    const totalWeight = remaining.reduce((sum, item) => sum + (weights[item.rarity] ?? 1), 0);
    let roll = random() * totalWeight;
    let index = remaining.length - 1;
    for (let i = 0; i < remaining.length; i++) {
      roll -= weights[remaining[i].rarity] ?? 1;
      if (roll <= 0) { index = i; break; }
    }
    picked.push(remaining.splice(index, 1)[0]);
  }
  return picked;
}
